import { useEffect } from 'react';
import type { GroupTimelineFamily } from '../utils/groupTimeline';
import { eventTargetIsEditable, isHubQuickSearchGloballyBlocked } from '../utils/isEditableTarget';

export const ALL_PROJECTS_TIMELINE_HOTKEY = 'l';
export const ALL_PROJECTS_PRIORITY_HOTKEY = 'p';
/** All Projects timeline only. A selected project uses S for the Suppliers tab. */
export const ALL_PROJECTS_SPLIT_HOTKEY = 's';
/** All Projects timeline, while a split is open. */
export const ALL_PROJECTS_MATCH_HOTKEY = 'm';
/** All Projects timeline. A selected project uses C for Change Orders. */
export const ALL_PROJECTS_CLEAR_FILTER_HOTKEY = 'c';

export const ALL_PROJECTS_FAMILY_HOTKEYS: ReadonlyArray<{
  key: string;
  family: GroupTimelineFamily;
  label: string;
}> = [
  { key: 'a', family: 'all', label: 'All' },
  { key: 'r', family: 'procurement', label: 'Procurement' },
  { key: 'p', family: 'production', label: 'Production' },
  { key: 'i', family: 'installation', label: 'Installation' },
];

export function allProjectsFamilyForHotkey(key: string): GroupTimelineFamily | null {
  const entry = ALL_PROJECTS_FAMILY_HOTKEYS.find((h) => h.key === key.toLowerCase());
  return entry?.family ?? null;
}

export function allProjectsFamilyHotkeyLabel(family: GroupTimelineFamily): string | null {
  const entry = ALL_PROJECTS_FAMILY_HOTKEYS.find((h) => h.family === family);
  return entry ? entry.key.toUpperCase() : null;
}

export interface UseAllProjectsHubHotkeysOptions {
  enabled?: boolean;
  /** `gantt` = All Projects timeline; anything else is the project list. */
  viewMode: 'table' | 'gantt' | string;
  blocked?: boolean;
  onToggleTimeline: () => void;
  onOpenPriority: () => void;
  onSelectFamily: (family: GroupTimelineFamily) => void;
  /** S toggles the second timeline. Omit for supplier and architect hubs. */
  onToggleSplit?: () => void;
  /** M toggles Match. Omit when the split is closed. */
  onToggleMatch?: () => void;
  /** C clears timeline filters. Omit for supplier and architect hubs. */
  onClearFilters?: () => void;
}

/**
 * All Projects list: L opens (or closes) the group timeline; P opens the work-queue
 * priority dialog. On the timeline, A / R / P / I switch All / Procurement / Production / Install.
 * S opens or closes the second timeline. M toggles Match while that split is open.
 * C clears timeline filters. A selected project keeps S for Suppliers and C for Change Orders.
 */
export function useAllProjectsHubHotkeys({
  enabled = true,
  viewMode,
  blocked = false,
  onToggleTimeline,
  onOpenPriority,
  onSelectFamily,
  onToggleSplit,
  onToggleMatch,
  onClearFilters,
}: UseAllProjectsHubHotkeysOptions): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.repeat) return;
      if (blocked || isHubQuickSearchGloballyBlocked()) return;
      if (eventTargetIsEditable(e)) return;

      const key = e.key.toLowerCase();
      const onTimeline = viewMode === 'gantt';

      if (key === ALL_PROJECTS_TIMELINE_HOTKEY) {
        e.preventDefault();
        onToggleTimeline();
        return;
      }

      if (onTimeline) {
        if (key === ALL_PROJECTS_SPLIT_HOTKEY && onToggleSplit) {
          e.preventDefault();
          onToggleSplit();
          return;
        }
        if (key === ALL_PROJECTS_MATCH_HOTKEY && onToggleMatch) {
          e.preventDefault();
          onToggleMatch();
          return;
        }
        if (key === ALL_PROJECTS_CLEAR_FILTER_HOTKEY && onClearFilters) {
          e.preventDefault();
          onClearFilters();
          return;
        }
        const family = allProjectsFamilyForHotkey(key);
        if (!family) return;
        e.preventDefault();
        onSelectFamily(family);
        return;
      }

      if (key === ALL_PROJECTS_PRIORITY_HOTKEY) {
        e.preventDefault();
        onOpenPriority();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, viewMode, blocked, onToggleTimeline, onOpenPriority, onSelectFamily, onToggleSplit, onToggleMatch, onClearFilters]);
}
